import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, BookOpen, Star, StarHalf, Book, Home } from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';

// Shared components
const FeatureCard = ({ icon, title, description, className = "" }: any) => {
  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<any>, { className: 'w-6 h-6 text-accent' });
    }
    return icon;
  };
  return (
    <div className={`p-6 bg-white rounded-xl shadow-lg ${className}`}>
      <div className="w-12 h-12 flex items-center justify-center rounded-full mb-4 bg-accent/10">{renderIcon()}</div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
};

const ProgramCard = ({ tag, title, description, link, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 ${className}`}>
    <span className="text-sm font-medium text-muted-foreground mb-4 block">{tag}</span>
    <h3 className="text-2xl font-semibold text-foreground mb-3">{title}</h3>
    <p className="text-muted-foreground mb-6">{description}</p>
    <Link to={link} className="text-accent font-semibold inline-flex items-center group">
      Learn more <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
    </Link>
  </div>
);

const SuccessStory = ({ name, exam, percentage, testimonial, initial, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg ${className}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
        <span className="text-lg font-bold text-accent">{initial}</span>
      </div>
      <div>
        <h3 className="text-lg font-semibold">{name}</h3>
        <p className="text-sm text-muted-foreground">{exam}</p>
      </div>
    </div>
    <span className="text-5xl font-bold text-foreground block mb-4">{percentage}</span>
    <p className="text-muted-foreground">"{testimonial}"</p>
  </div>
);

const TestimonialCard = ({ rating, text, name, role, initials, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg ${className}`}>
    <div className="flex items-center mb-4">
      {[...Array(Math.floor(rating))].map((_, i) => <Star key={i} className="w-4 h-4 text-accent fill-accent" />)}
      {rating % 1 !== 0 && <StarHalf className="w-4 h-4 text-accent" />}
    </div>
    <p className="text-foreground mb-6">"{text}"</p>
    <div className="flex items-center">
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mr-3">
        <span className="font-medium">{initials}</span>
      </div>
      <div>
        <h4 className="font-semibold">{name}</h4>
        <p className="text-sm text-muted-foreground">{role}</p>
      </div>
    </div>
  </div>
);

/**
 * Home Light Blue Variant - Light blue (bg-muted) replaces all white sections
 * Route: /home-light-blue
 */
const HomeLightBlue: React.FC = () => {
  const features = [
    { icon: <Users />, title: 'Personalized Approach', description: "Individualized learning plans tailored to each student's needs." },
    { icon: <Award />, title: 'Expert Teachers', description: 'Qualified educators with extensive experience and passion.' },
    { icon: <BookOpen />, title: 'Multiple Curricula', description: 'CBSE, IGCSE, IB, Federal, and Bangladesh Board support.' },
  ];

  const programs = [
    { tag: 'Grades K.G-12', title: 'Core Subjects', description: 'Mathematics, Physics, Chemistry, Biology, English, and more.', link: '/services' },
    { tag: 'All Levels', title: 'Specialized Programs', description: 'Crash courses, entrance test prep, and foundation courses.', link: '/services' },
    { tag: 'Summer', title: 'Summer Courses', description: 'Keep the learning momentum going during summer.', link: '/services' },
  ];

  const successStories = [
    { name: "Lalenthika", exam: "CBSE Board Exam", percentage: "93.4%", testimonial: "Tuition Point helped me excel in my board exams.", initial: "L" },
    { name: "Keerthana", exam: "CBSE Board Exam", percentage: "93%", testimonial: "Personalized attention made all the difference.", initial: "K" }
  ];

  const testimonials = [
    { rating: 5, text: "Remarkable improvement in my daughter's academic skills over 3 months.", name: "Ponnu Menu", role: "Parent", initials: "PM" },
    { rating: 5, text: "Teaching methods have really helped my child grasp difficult concepts.", name: "Aatha Sharma", role: "Parent", initials: "AS" },
    { rating: 4.5, text: "Thanks to coaching, my kids gained admission to BPS.", name: "Kesavan Nagaraj", role: "Parent", initials: "KN" }
  ];

  const quickLinks = [
    { icon: <Book className="w-6 h-6 text-cyan-700" />, title: "Curriculum", description: "Explore our curricula.", link: "/curriculum" },
    { icon: <Users className="w-6 h-6 text-cyan-700" />, title: "Parent Support", description: "Comprehensive support systems.", link: "/parent-support" },
    { icon: <Home className="w-6 h-6 text-cyan-700" />, title: "Our Campus", description: "Tour our facilities.", link: "/campus" }
  ];

  // This variant: light blue replaces white
  const bgLight = "bg-muted"; // #F1F8FF

  return (
    <div className={`${bgLight} text-foreground min-h-screen`}>
      <SEO title="Home Light Blue Variant | Tuition Point Qatar" />

      {/* Demo Label */}
      <div className="bg-cyan-600 text-white text-center py-2 text-sm font-medium">
        🎨 Variant: <strong>LIGHT BLUE</strong> - White sections replaced with bg-muted (#F1F8FF)
      </div>

      {/* Variant Navigation */}
      <div className="bg-white py-3 border-b">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/home-white" className="px-3 py-1.5 bg-white border border-gray-300 rounded font-medium text-sm hover:bg-gray-100">White</Link>
            <Link to="/home-light-blue" className="px-3 py-1.5 bg-sky-100 border-2 border-gray-800 rounded font-medium text-sm">Light Blue ●</Link>
            <Link to="/home-blue" className="px-3 py-1.5 bg-cyan-200 border border-cyan-400 rounded font-medium text-sm hover:bg-cyan-300">Blue</Link>
            <Link to="/home-dark-blue" className="px-3 py-1.5 bg-cyan-500 text-white rounded font-medium text-sm hover:bg-cyan-600">Dark Blue</Link>
            <Link to="/" className="px-3 py-1.5 bg-gray-800 text-white rounded font-medium text-sm hover:bg-gray-900">← Main</Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`pt-16 pb-20 md:pt-20 md:pb-24 ${bgLight}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-muted-foreground mb-2 block">Licensed by the Ministry of Education & Higher Education</span>
              <h1 className="text-foreground text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Expert K-12 Tutoring in <br/>Al Wakra, Qatar
              </h1>
              <p className="text-lg text-foreground mb-8 max-w-lg">
                Welcome to <span className="brand-orange bg-yellow-100 rounded-full p-1 px-3">Tuition Point Education Center</span>. Personalized tutoring for CBSE, IGCSE, IB, Federal & Bangladesh Board students.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/enrollment" className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center">
                  Enroll Today <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/contact" className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3 px-6 rounded-lg shadow-md">
                  Contact Us
                </Link>
              </div>
            </div>
            <div>
              <img src={asset('Home/home.jpg')} alt="Students" className="w-full h-auto rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - keeps accent */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Tuition Point Qatar?</h2>
            <p className="text-lg text-white">Al Wakra's trusted education center.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((f, i) => <FeatureCard key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* Programs Section - light blue */}
      <section className={`py-24 ${bgLight}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-foreground mb-4">Our Programs</h2>
            <p className="text-lg text-muted-foreground">Comprehensive academic support.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((p, i) => <ProgramCard key={i} {...p} />)}
          </div>
        </div>
      </section>

      {/* Success Stories - keeps accent */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Success Stories</h2>
            <p className="text-lg text-white">Remarkable results through dedication.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {successStories.map((s, i) => <SuccessStory key={i} {...s} />)}
          </div>
        </div>
      </section>

      {/* Testimonials - light blue */}
      <section className={`py-24 ${bgLight}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-accent fill-accent" />)}
              <span className="ml-2 text-lg font-medium text-muted-foreground">4.9/5</span>
            </div>
            <h2 className="text-4xl font-semibold text-foreground mb-4">What Parents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} />)}
          </div>
        </div>
      </section>

      {/* Quick Links - keeps accent */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Explore More</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickLinks.map((link, i) => (
              <Link to={link.link} key={i} className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-start hover:-translate-y-1 transition-transform">
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">{link.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{link.title}</h3>
                <p className="text-muted-foreground">{link.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeLightBlue;
